/**
 * useStartNewChat
 * Creates a chat session (optionally inside a project) from the first message /
 * realtime-voice request and navigates to it. Shared by NewChatPage and project pages.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgents } from '../contexts/AgentsContext';
import { useNotification } from './useNotification';
import { useStoredChatExecutionMode } from './useStoredChatExecutionMode';
import { useSidebarConversations } from '../components/layout/useSidebarConversations';
import { createChatSession, listAgents } from '../services/api';
import type { Agent, ChatSession } from '../types';
import type { ChatExecutionMode } from './useChatAgent';

const isAnonymousLimitResponse = (response: { errorCode?: string; error?: string }) =>
  response.errorCode === 'ANONYMOUS_LIMIT_EXCEEDED' ||
  response.error === 'ANONYMOUS_LIMIT_EXCEEDED';

interface UseStartNewChatOptions {
  /** Project public ID the new conversation should belong to. */
  projectId?: string;
}

export const useStartNewChat = ({ projectId }: UseStartNewChatOptions = {}) => {
  const navigate = useNavigate();
  const { agents, loading } = useAgents();
  const { error: showError } = useNotification();
  const { addOrUpdateConversation } = useSidebarConversations();
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [executionMode, setExecutionMode] = useStoredChatExecutionMode();

  const resolveAgent = async (selectedAgent?: Agent | null): Promise<Agent | null> => {
    if (selectedAgent && agents.some((agent) => agent.publicId === selectedAgent.publicId)) {
      return selectedAgent;
    }

    // Pick the default agent, otherwise fall back to the first available one
    const contextAgent = agents.find((a) => a.isDefault) ?? agents[0];
    if (contextAgent) {
      return contextAgent;
    }

    const response = await listAgents();
    if (!response.success || !response.data) {
      return null;
    }

    return response.data.find((a) => a.isDefault) ?? response.data[0] ?? null;
  };

  const handleSend = async (
    message: string,
    files?: File[],
    selectedAgent?: Agent | null,
    mode: ChatExecutionMode = executionMode
  ) => {
    if (!message.trim() || isStartingChat || loading) return;

    const file = files?.[0] ?? null;
    setExecutionMode(mode);
    setIsStartingChat(true);
    try {
      const agent = await resolveAgent(selectedAgent);

      if (!agent) {
        const sessionResponse = await createChatSession(null, 'New Chat', projectId);

        if (!sessionResponse.success || !sessionResponse.data) {
          if (isAnonymousLimitResponse(sessionResponse)) {
            return;
          }
          throw new Error(sessionResponse.error || 'Failed to create chat session');
        }

        addOrUpdateConversation(sessionResponse.data);

        navigate(`/chat/${sessionResponse.data.id}`, {
          state: {
            initialMessage: message,
            initialFile: file ?? null,
            initialMode: mode,
            initialSession: sessionResponse.data,
            initialAgent: null,
          },
        });
        return;
      }

      const sessionResponse = await createChatSession(
        agent.publicId,
        `Chat with ${agent.name}`,
        projectId
      );

      if (!sessionResponse.success || !sessionResponse.data) {
        if (isAnonymousLimitResponse(sessionResponse)) {
          return;
        }
        throw new Error(sessionResponse.error || 'Failed to create chat session');
      }

      const session = sessionResponse.data as ChatSession & {
        publicId?: string;
      };
      const sessionId = session.publicId;

      if (!sessionId) {
        throw new Error('Created chat session is missing publicId');
      }

      addOrUpdateConversation(session);

      // Navigate inside the protected layout so the global sidebar stays mounted.
      navigate(`/chat/${sessionId}`, {
        state: {
          initialMessage: message,
          initialFile: file ?? null,
          initialMode: mode,
          initialSession: session,
          initialAgent: agent,
        },
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to start chat');
    } finally {
      setIsStartingChat(false);
    }
  };

  const handleStartRealtimeVoice = async (
    selectedAgent?: Agent | null,
    mode: ChatExecutionMode = executionMode
  ) => {
    if (isStartingChat || loading) return;

    setExecutionMode(mode);
    setIsStartingChat(true);
    try {
      const agent = await resolveAgent(selectedAgent);
      const sessionResponse = agent
        ? await createChatSession(agent.publicId, `Chat with ${agent.name}`, projectId)
        : await createChatSession(null, 'New Chat', projectId);

      if (!sessionResponse.success || !sessionResponse.data) {
        if (isAnonymousLimitResponse(sessionResponse)) {
          return;
        }
        throw new Error(sessionResponse.error || 'Failed to create chat session');
      }

      const session = sessionResponse.data as ChatSession & {
        publicId?: string;
      };
      const sessionId = session.publicId;

      if (!sessionId) {
        throw new Error('Created chat session is missing publicId');
      }

      addOrUpdateConversation(session);

      navigate(`/chat/${sessionId}`, {
        state: {
          initialMode: mode,
          initialStartRealtimeVoice: true,
          initialSession: session,
          initialAgent: agent,
        },
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to start realtime voice chat');
    } finally {
      setIsStartingChat(false);
    }
  };

  return {
    isStartingChat,
    isBusy: isStartingChat || loading,
    executionMode,
    setExecutionMode,
    handleSend,
    handleStartRealtimeVoice,
  };
};
